import { useState, useRef, useCallback, useEffect } from "react";
import {
  Tabs,
  Tab,
  Button,
  Form,
  InputGroup,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useMutation, useQuery } from "react-query";
import { useAuth } from "../../hooks/useAuth";
import { fetchActionsUtil } from "../../utils/helpers";
import { toast } from "react-toastify";
import { LockIcon } from "../Icons";
import { FaUnlockAlt } from "react-icons/fa";

// ─── Constants ────────────────────────────────────────────────────────────────

const defaultBanks = [];

const defaultCompany = {};

const BUILTIN_VARS = [
  { key: "daysRemaining", label: "{{Days Remaining}}" },
  { key: "company.Phone", label: "{{Company Phone}}" },
  { key: "company.Email", label: "{{Company Email}}" },
  { key: "banks", label: "{{Banks Account Details}}" },
  { key: "company.CompName", label: "{{Company Name}}" },
  { key: "customer.FirstName", label: "{{Customer First Name}}" },
  { key: "customer.LastName", label: "{{Customer Last Name}}" },
];

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function renderTemplate(tmpl, { subject, daysRemaining, company, banks }) {
  const banksHtml = banks
    .map(
      (b) => `<p><b>${b.AccountName}</b><br/>${b.BankName}: ${b.AccountNo}</p>`,
    )
    .join("");
  return tmpl

    .replace(/\{\{daysRemaining\}\}/g, daysRemaining)
    .replace(/\{\{company\.Phone\}\}/g, company.Phone)
    .replace(/\{\{company\.Email\}\}/g, company.Email)
    .replace(/\{\{banks\}\}/g, banksHtml)
    .replace(/\{\{company\.CompName\}\}/g, company?.CompName || "")
    .replace(/\{\{customer\.FirstName\}\}/g, "John")
    .replace(/\{\{customer\.LastName\}\}/g, "Doe")
    .replace(/\{\{customer\.Email\}\}/g, "member@example.com");
}

// function templateToEditorHtml(tmpl) {
//   return tmpl.replace(/(\{\{[^}]+\}\})/g, (m) => {
//     const escaped = m.replace(/"/g, "&quot;");
//     return `<span class="var-chip" contenteditable="false" data-var="${escaped}">${m}<button class="var-chip-del" data-del="true">×</button></span>`;
//   });
// }

function templateToEditorHtml(tmpl) {
  // Split on HTML tags to process only text nodes, not attribute values
  return tmpl.replace(/(<[^>]*>)|(\{\{[^}]+\}\})/g, (match, tag, variable) => {
    if (tag) {
      // It's an HTML tag — replace any {{var}} inside attributes with just the plain value
      return tag.replace(/\{\{([^}]+)\}\}/g, (m) => m); // leave as-is inside tags
    }
    if (variable) {
      // It's a {{var}} in text content — wrap it in a chip
      const escaped = variable.replace(/"/g, "&quot;");
      return `<span class="var-chip" contenteditable="false" data-var="${escaped}">${variable}<button class="var-chip-del" data-del="true">×</button></span>`;
    }
    return match;
  });
}

function editorToTemplate(editorEl) {
  const clone = editorEl.cloneNode(true);
  clone.querySelectorAll(".var-chip").forEach((span) => {
    const varStr = span.dataset.var.replace(/&quot;/g, '"');
    span.parentNode.replaceChild(document.createTextNode(varStr), span);
  });
  return clone.innerHTML;
}

// Converts editor HTML into a JS template literal string.
// {{daysRemaining}}  →  ${daysRemaining}
// {{company.Phone}}  →  ${company.Phone}
// {{banks}}          →  ${banks}
// Backticks and existing ${ in the raw HTML are escaped so the output is safe.

// you will need to convert this on the server to template lateral

function getVarSpans(editorEl) {
  return Array.from(editorEl.querySelectorAll(".var-chip"));
}

function selectionContainsVar(range, editorEl) {
  return getVarSpans(editorEl).some((span) => range.intersectsNode(span));
}

function rangeWouldDeleteVar(key, editorEl) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);

  if (!range.collapsed) return selectionContainsVar(range, editorEl);

  const { startContainer, startOffset } = range;

  if (key === "Backspace") {
    if (startContainer.nodeType === Node.ELEMENT_NODE) {
      const nodeBefore = startContainer.childNodes[startOffset - 1];
      if (nodeBefore?.classList?.contains("var-chip")) return true;
    }
    if (startContainer.nodeType === Node.TEXT_NODE && startOffset === 0) {
      const prev = startContainer.previousSibling;
      if (prev?.classList?.contains("var-chip")) return true;
    }
  }

  if (key === "Delete") {
    if (startContainer.nodeType === Node.ELEMENT_NODE) {
      const nodeAfter = startContainer.childNodes[startOffset];
      if (nodeAfter?.classList?.contains("var-chip")) return true;
    }
    if (
      startContainer.nodeType === Node.TEXT_NODE &&
      startOffset === startContainer.length
    ) {
      const next = startContainer.nextSibling;
      if (next?.classList?.contains("var-chip")) return true;
    }
  }

  return false;
}

// ─── Toolbar button ───────────────────────────────────────────────────────────

function FmtBtn({ onClick, title, children, active }) {
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip>{title}</Tooltip>}>
      <Button
        variant={active ? "primary" : "outline-secondary"}
        size="sm"
        onMouseDown={(e) => {
          e.preventDefault();
          onClick();
        }}
        className="me-1 mb-1 px-2 py-1"
        style={{ minWidth: 30, fontSize: 13 }}
      >
        {children}
      </Button>
    </OverlayTrigger>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function EmailTemplateEditor({ onOpen }) {
  const [activeTab, setActiveTab] = useState("editor");
  const [subject, setSubject] = useState("Subscription Renewal Notice");
  const [banks, setBanks] = useState(defaultBanks);
  const [company, setCompany] = useState(defaultCompany);
  const [daysRemaining, setDaysRemaining] = useState("14");
  const [, setCopySuccess] = useState(false);
  const [variablesLocked, setVariablesLocked] = useState(true);
  const [activeFormats, setActiveFormats] = useState({});
  const [defaultTemplate, setDefaultTemplate] = useState("");
  const [logo, setLogo] = useState("");

  const editorRef = useRef(null);
  const savedRangeRef = useRef(null);
  const initializedRef = useRef(false);

  const { backendUrl } = useAuth();

  // Fetch auto email templates and set up banks and company info
  useQuery(
    ["GET_AUTO_EMAIL_TEMPLATES"],
    () =>
      fetchActionsUtil(
        `${backendUrl}/api/customers/get-auto-email-templates?type=subscription`,
        "GET",
      ),
    {
      enabled: !!backendUrl,
      onSuccess: (data) => {
        if (data?.data) {
          // Set up banks from template data
          if (data?.data?.banks && Array.isArray(data?.data?.banks)) {
            setBanks(data?.data?.banks);
          }
          // Set up company info from template data
          if (data?.data?.company) {
            setCompany(data?.data?.company);
          }
          // Set up subject if available
          if (data?.data?.subject) {
            setSubject(data?.data?.subject);
          }
          if (data?.data?.content) {
            setDefaultTemplate(data?.data?.content);
          }
          if (data?.data?.logo) {
            setLogo(data?.data?.logo);
          }
        }
      },
    },
  );

  // Keep latest values in refs so event listeners don't go stale
  const lockedRef = useRef(variablesLocked);
  lockedRef.current = variablesLocked;

  const subjectRef = useRef(subject);
  subjectRef.current = subject;
  const banksRef = useRef(banks);
  banksRef.current = banks;
  const companyRef = useRef(company);
  companyRef.current = company;
  const daysRef = useRef(daysRemaining);
  daysRef.current = daysRemaining;

  // ── Init editor content once ─────────────────────────────────────────────
  const initEditor = useCallback((node) => {
    if (node && !initializedRef.current) {
      initializedRef.current = true;
      editorRef.current = node;
      node.innerHTML = templateToEditorHtml(defaultTemplate);
      updateDelVisibility(node, true);
    }
  }, []);

  // ── Update editor content when template data is fetched ──────────────────
  useEffect(() => {
    if (editorRef.current && defaultTemplate) {
      editorRef.current.innerHTML = templateToEditorHtml(defaultTemplate);
      updateDelVisibility(editorRef.current, variablesLocked);
    }
  }, [defaultTemplate, variablesLocked]);

  // ── Update del button visibility on all var chips ────────────────────────
  function updateDelVisibility(editorEl, locked) {
    editorEl.querySelectorAll(".var-chip-del").forEach((btn) => {
      btn.style.display = locked ? "none" : "inline-flex";
    });
  }

  // ── Sync toolbar active states ───────────────────────────────────────────
  const syncFormats = () => {
    setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      strikeThrough: document.queryCommandState("strikeThrough"),
      insertUnorderedList: document.queryCommandState("insertUnorderedList"),
      insertOrderedList: document.queryCommandState("insertOrderedList"),
      justifyLeft: document.queryCommandState("justifyLeft"),
      justifyCenter: document.queryCommandState("justifyCenter"),
      justifyRight: document.queryCommandState("justifyRight"),
    });
  };

  // ── Selection helpers ────────────────────────────────────────────────────
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
    syncFormats();
  };

  const restoreSelection = () => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    if (savedRangeRef.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    }
  };

  // ── execCommand ──────────────────────────────────────────────────────────
  const execCmd = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
    syncFormats();
  };

  // ── Keyboard and beforeinput guards ─────────────────────────────────────
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;

    const onKeyDown = (e) => {
      if (!lockedRef.current) return;

      // Block cut containing a var
      if ((e.ctrlKey || e.metaKey) && e.key === "x") {
        const sel = window.getSelection();
        if (sel && !sel.isCollapsed && sel.rangeCount > 0) {
          if (selectionContainsVar(sel.getRangeAt(0), el)) {
            e.preventDefault();
            return;
          }
        }
      }

      // Block Backspace / Delete adjacent to or selecting a var
      if (e.key === "Backspace" || e.key === "Delete") {
        if (rangeWouldDeleteVar(e.key, el)) {
          e.preventDefault();
          return;
        }
      }
    };

    const onBeforeInput = (e) => {
      if (!lockedRef.current) return;

      const destructive = [
        "deleteContentBackward",
        "deleteContentForward",
        "deleteByCut",
        "deleteByDrag",
        "deleteContent",
        "deleteHardLineBackward",
        "deleteHardLineForward",
        "deleteSoftLineBackward",
        "deleteSoftLineForward",
        "deleteWordBackward",
        "deleteWordForward",
      ];
      if (!destructive.includes(e.inputType)) return;

      // Word/line deletes — always block when locked (can't predict range)
      if (
        e.inputType.startsWith("deleteWord") ||
        e.inputType.startsWith("deleteSoftLine") ||
        e.inputType.startsWith("deleteHardLine")
      ) {
        e.preventDefault();
        return;
      }

      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);

      if (!range.collapsed && selectionContainsVar(range, el)) {
        e.preventDefault();
        return;
      }

      const { startContainer, startOffset } = range;
      if (range.collapsed) {
        if (e.inputType === "deleteContentBackward") {
          if (startContainer.nodeType === Node.ELEMENT_NODE) {
            const nb = startContainer.childNodes[startOffset - 1];
            if (nb?.classList?.contains("var-chip")) {
              e.preventDefault();
              return;
            }
          }
          if (startContainer.nodeType === Node.TEXT_NODE && startOffset === 0) {
            const prev = startContainer.previousSibling;
            if (prev?.classList?.contains("var-chip")) {
              e.preventDefault();
              return;
            }
          }
        }
        if (e.inputType === "deleteContentForward") {
          if (startContainer.nodeType === Node.ELEMENT_NODE) {
            const na = startContainer.childNodes[startOffset];
            if (na?.classList?.contains("var-chip")) {
              e.preventDefault();
              return;
            }
          }
          if (
            startContainer.nodeType === Node.TEXT_NODE &&
            startOffset === startContainer.length
          ) {
            const next = startContainer.nextSibling;
            if (next?.classList?.contains("var-chip")) {
              e.preventDefault();
              return;
            }
          }
        }
      }
    };

    // Click handler for del buttons inside var chips
    const onClick = (e) => {
      if (e.target.dataset.del && !lockedRef.current) {
        e.preventDefault();
        const span = e.target.closest(".var-chip");
        if (span) span.remove();
      }
    };

    el.addEventListener("keydown", onKeyDown);
    el.addEventListener("beforeinput", onBeforeInput);
    el.addEventListener("click", onClick);
    return () => {
      el.removeEventListener("keydown", onKeyDown);
      el.removeEventListener("beforeinput", onBeforeInput);
      el.removeEventListener("click", onClick);
    };
  }, []);

  // ── Toggle lock ──────────────────────────────────────────────────────────
  const toggleLock = () => {
    const next = !variablesLocked;
    setVariablesLocked(next);
    lockedRef.current = next;
    if (editorRef.current) updateDelVisibility(editorRef.current, next);
  };

  // ── Insert variable ──────────────────────────────────────────────────────
  const insertVar = (varStr) => {
    if (variablesLocked) return;
    restoreSelection();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);

    const span = document.createElement("span");
    span.className = "var-chip";
    span.contentEditable = "false";
    span.dataset.var = varStr;

    const label = document.createTextNode(varStr);
    span.appendChild(label);

    const del = document.createElement("button");
    del.className = "var-chip-del";
    del.dataset.del = "true";
    del.textContent = "×";
    del.style.display = variablesLocked ? "none" : "inline-flex";
    span.appendChild(del);

    range.deleteContents();
    range.insertNode(span);
    range.setStartAfter(span);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  };

  // ── Get rendered HTML (for preview) ─────────────────────────────────────
  const getRenderedHtml = () => {
    const el = editorRef.current;
    if (!el) return "";
    const tmpl = `  <div
      style="
        margin-top: 1rem;
        font-family: Larsseit, Arial, Helvetica, sans-serif;
        color: black;
        width: 100%;
        max-width: 600px;
        margin-left: auto;
        margin-right: auto;
      "
    >

     <div
        class="container"
        style="width: 100%; margin: 0 auto; background-color: #fff; padding: 0">
        <div
          style="
            width: 120px;
            margin-bottom: 10px;
            margin-left: auto;
            margin-right: auto;
          ">
           <img style="text-align: center" src="${logo}" width="120" />
          </div>

          <center>
         
                    <div style="font-size: 1.2rem; font-weight: bold;">${company?.CompName ||
                      ""}</div>
                    <div >${company?.AddressLine1 || ""}</div>
                    <div >${company?.Email || ""}</div>
                    <div >${company?.Phone || ""}</div>


            <div
            style="
              border-top: 2px solid blue;
              background-color: #f4f5f9;
              padding: 0.5rem 0;
            ">
            <p style="font-size: 1.2rem; font-weight: bold;">${subject}</p>
          </div>
           <div
            style="
              background-color: #dce9f1;
              padding: 1.5rem 0;
              
            ">
            <p style="font-size: 0.7rem">Powered By InvexERP</p>
          </div>
 </center>

                    ${editorToTemplate(el)}

    </div>`;
    return renderTemplate(tmpl, {
      subject: subjectRef.current,
      daysRemaining: daysRef.current,
      company: companyRef.current,
      banks: banksRef.current,
    });
  };

  const saveTemplateMutation = useMutation(
    (payload) =>
      fetchActionsUtil(
        `${backendUrl}/api/customers/save-auto-email-templates`,
        "POST",
        "",
        payload,
      ),
    {
      onSuccess: (data) => {
        onOpen(false);
        toast.success(data?.message);
      },
      onError: (error) => {
        toast.error(error?.message);
      },
    },
  );

  // ── Copy as JS template literal ──────────────────────────────────────────
  // Output looks like:
  //   `<p>Dear Member,</p>...<p>...within ${daysRemaining} days...</p>...`
  // so you can eval / assign it directly in JS and inject variables.
  const saveTemplate = async () => {
    const el = editorRef.current;

    saveTemplateMutation.mutate({
      subject,
      content: editorToTemplate(el),
      type: "subscription",
    });

    // if (!el) return;
    // const literal = editorToTemplateLiteral(el);
    // try {
    //   await navigator.clipboard.writeText(literal);
    // } catch {
    //   const ta = document.createElement("textarea");
    //   ta.value = literal;
    //   ta.style.cssText = "position:fixed;opacity:0;pointer-events:none";
    //   document.body.appendChild(ta);
    //   ta.focus();
    //   ta.select();
    //   document.execCommand("copy");
    //   document.body.removeChild(ta);
    // }
    // setCopySuccess(true);
    // setTimeout(() => setCopySuccess(false), 1800);
  };

  // ── Bank helpers ─────────────────────────────────────────────────────────
  const addBank = () =>
    setBanks((p) => [...p, { AccountName: "", BankName: "", AccountNo: "" }]);
  const removeBank = (i) => setBanks((p) => p.filter((_, idx) => idx !== i));
  const updateBank = (i, field, value) =>
    setBanks((p) =>
      p.map((b, idx) => (idx === i ? { ...b, [field]: value } : b)),
    );

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      {/* Scoped styles */}
      <style>{`
        .var-chip {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          background: #FEF3C7;
          color: #92400E;
          border-radius: 3px;
          padding: 1px 4px;
          font-family: monospace;
          font-size: 12px;
          user-select: none;
          line-height: 1.4;
        }
        .var-chip-del {
          display: none;
          align-items: center;
          justify-content: center;
          width: 13px;
          height: 13px;
          border-radius: 50%;
          background: #d97706;
          color: #fff;
          font-size: 10px;
          line-height: 1;
          cursor: pointer;
          border: none;
          padding: 0;
          flex-shrink: 0;
        }
        .var-chip-del:hover { background: #b45309; }
        #email-editor p          { margin-bottom: 0.75rem; }
        #email-editor h1         { font-size: 1.6rem; font-weight: 700; margin-bottom: 0.5rem; }
        #email-editor h2         { font-size: 1.3rem; font-weight: 700; margin-bottom: 0.5rem; }
        #email-editor h3         { font-size: 1.1rem; font-weight: 600; margin-bottom: 0.5rem; }
        #email-editor blockquote { border-left: 3px solid #d1d5db; padding-left: 1rem; color: #6b7280; margin: 0.75rem 0; font-style: italic; }
        #email-editor ul         { padding-left: 1.5rem; list-style: disc; margin-bottom: 0.75rem; }
        #email-editor ol         { padding-left: 1.5rem; list-style: decimal; margin-bottom: 0.75rem; }
        #email-editor a          { color: #2563eb; text-decoration: underline; }
        .preview-body p          { margin-bottom: 0.75rem; }
        .preview-body a          { color: #2563eb; text-decoration: underline; }
        .var-insert-chip {
          display: inline-flex;
          align-items: center;
          padding: 3px 10px;
          border-radius: 99px;
          border: 1px solid #d1d5db;
          background: #f9fafb;
          font-size: 12px;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.15s;
          user-select: none;
        }
        .var-insert-chip:hover:not(.disabled) {
          background: #eff6ff;
          color: #1d4ed8;
          border-color: #bfdbfe;
        }
        .var-insert-chip.disabled {
          opacity: 0.4;
          cursor: not-allowed;
          pointer-events: none;
        }
      `}</style>

      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
        {/* ── EDITOR TAB ─────────────────────────────────────────────────── */}
        <Tab eventKey="editor" title="Editor">
          {/* Subject input - centered at top */}
          <div className="mb-3">
            <Form.Label className=" fw-semibold mb-2">Email Subject</Form.Label>
            <Form.Control
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject..."
              className="   fw-bold  fs-3"
            />
          </div>

          {/* Variable insert chips */}
          <p
            className="text-uppercase text-muted small fw-semibold mb-2"
            style={{ letterSpacing: "0.06em", fontSize: 11 }}
          >
            Insert variable
          </p>
          <div className="d-flex flex-wrap gap-2 mb-3">
            {BUILTIN_VARS.map(({ key, label }) => (
              <span
                key={key}
                className={`var-insert-chip${
                  variablesLocked ? " disabled" : ""
                }`}
                title={
                  variablesLocked
                    ? "Unlock variables to insert"
                    : `Insert ${label}`
                }
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertVar(`{{${key}}}`);
                }}
              >
                <code style={{ fontFamily: "monospace", fontSize: 11 }}>
                  {label}
                </code>
              </span>
            ))}

            {/* Lock toggle */}
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  {variablesLocked
                    ? "Click to allow variable editing"
                    : "Click to lock variables"}
                </Tooltip>
              }
            >
              <Button
                size="sm"
                variant={"outline-secondary"}
                onClick={toggleLock}
                className="d-flex align-items-center gap-1 px-2 py-1 ms-2"
                style={{ fontSize: 11, fontWeight: 500 }}
              >
                {variablesLocked ? (
                  <LockIcon />
                ) : (
                  <FaUnlockAlt size={20} fill="#2463AE" />
                )}
              </Button>
            </OverlayTrigger>
          </div>

          {/* Toolbar */}
          <div className="bg-white border border-bottom-0 rounded-top p-3 d-flex flex-wrap align-items-center gap-2">
            {/* Font size */}
            <Form.Select
              size="sm"
              style={{ width: "auto", fontSize: 12 }}
              onChange={(e) => execCmd("fontSize", e.target.value)}
            >
              {[
                ["1", "8px"],
                ["2", "10px"],
                ["3", "12px"],
                ["4", "14px"],
                ["5", "18px"],
                ["6", "24px"],
                ["7", "36px"],
              ].map(([v, lbl]) => (
                <option key={v} value={v}>
                  {lbl}
                </option>
              ))}
            </Form.Select>

            <div className="vr mx-1" />

            <FmtBtn
              active={activeFormats.bold}
              onClick={() => execCmd("bold")}
              title="Bold"
            >
              {" "}
              <b>B</b>
            </FmtBtn>
            <FmtBtn
              active={activeFormats.italic}
              onClick={() => execCmd("italic")}
              title="Italic"
            >
              {" "}
              <i>I</i>
            </FmtBtn>
            <FmtBtn
              active={activeFormats.underline}
              onClick={() => execCmd("underline")}
              title="Underline"
            >
              {" "}
              <u>U</u>
            </FmtBtn>
            <FmtBtn
              active={activeFormats.strikeThrough}
              onClick={() => execCmd("strikeThrough")}
              title="Strikethrough"
            >
              <s>S</s>
            </FmtBtn>

            <div className="vr mx-1" />

            <FmtBtn
              onClick={() => execCmd("formatBlock", "h1")}
              title="Heading 1"
            >
              <span className="fw-bold" style={{ fontSize: 12 }}>
                H1
              </span>
            </FmtBtn>
            <FmtBtn
              onClick={() => execCmd("formatBlock", "h2")}
              title="Heading 2"
            >
              <span className="fw-bold" style={{ fontSize: 12 }}>
                H2
              </span>
            </FmtBtn>
            <FmtBtn
              onClick={() => execCmd("formatBlock", "h3")}
              title="Heading 3"
            >
              <span className="fw-bold" style={{ fontSize: 12 }}>
                H3
              </span>
            </FmtBtn>
            <FmtBtn
              onClick={() => execCmd("formatBlock", "p")}
              title="Paragraph"
            >
              <span style={{ fontSize: 12 }}>¶</span>
            </FmtBtn>

            <div className="vr mx-1" />

            <FmtBtn
              active={activeFormats.justifyLeft}
              onClick={() => execCmd("justifyLeft")}
              title="Align left"
            >
              ≡
            </FmtBtn>
            <FmtBtn
              active={activeFormats.justifyCenter}
              onClick={() => execCmd("justifyCenter")}
              title="Align center"
            >
              ⋮
            </FmtBtn>
            <FmtBtn
              active={activeFormats.justifyRight}
              onClick={() => execCmd("justifyRight")}
              title="Align right"
            >
              ≡
            </FmtBtn>

            <div className="vr mx-1" />

            <FmtBtn
              active={activeFormats.insertUnorderedList}
              onClick={() => execCmd("insertUnorderedList")}
              title="Bullet list"
            >
              • —
            </FmtBtn>
            <FmtBtn
              active={activeFormats.insertOrderedList}
              onClick={() => execCmd("insertOrderedList")}
              title="Numbered list"
            >
              1.
            </FmtBtn>

            <div className="vr mx-1" />

            <FmtBtn
              onClick={() => {
                const url = prompt("Enter URL:", "https://");
                if (url) execCmd("createLink", url);
              }}
              title="Insert link"
            >
              🔗
            </FmtBtn>
            <FmtBtn onClick={() => execCmd("unlink")} title="Remove link">
              🔗̶
            </FmtBtn>

            <div className="vr mx-1" />

            <FmtBtn onClick={() => execCmd("undo")} title="Undo">
              ↩
            </FmtBtn>
            <FmtBtn onClick={() => execCmd("redo")} title="Redo">
              ↪
            </FmtBtn>
            <FmtBtn
              onClick={() => execCmd("removeFormat")}
              title="Clear formatting"
            >
              ✕
            </FmtBtn>

            <div className="vr mx-1" />
          </div>

          {/* Editable area */}
          <div
            id="email-editor"
            ref={initEditor}
            contentEditable
            suppressContentEditableWarning
            spellCheck
            onMouseUp={saveSelection}
            onKeyUp={saveSelection}
            className="border rounded-bottom p-3"
            style={{
              fontSize: 14,
              lineHeight: 1.8,
              outline: "none",
              minHeight: 300,
            }}
          />

          <div className="d-flex justify-content-end mt-3">
            <Button onClick={saveTemplate}>
              {" "}
              {saveTemplateMutation.isLoading ? "Saving..." : "Save"}
            </Button>
          </div>
        </Tab>

        {/* ── VARIABLES TAB ──────────────────────────────────────────────── */}
        {/* <Tab eventKey="variables" title="Variables">
          <p
            className="text-uppercase text-muted small fw-semibold mb-2"
            style={{ letterSpacing: "0.06em", fontSize: 11 }}
          >
            Company details
          </p>
          <div className="row g-2 mb-4">
            <div className="col-md-4">
              <Form.Label className="small text-muted">
                Days remaining
              </Form.Label>
              <Form.Control
                type="number"
                size="sm"
                value={daysRemaining}
                onChange={(e) => setDaysRemaining(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <Form.Label className="small text-muted">Phone</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                value={company.Phone}
                onChange={(e) =>
                  setCompany((c) => ({ ...c, Phone: e.target.value }))
                }
              />
            </div>
            <div className="col-md-4">
              <Form.Label className="small text-muted">Email</Form.Label>
              <Form.Control
                type="email"
                size="sm"
                value={company.Email}
                onChange={(e) =>
                  setCompany((c) => ({ ...c, Email: e.target.value }))
                }
              />
            </div>
          </div>

          <p
            className="text-uppercase text-muted small fw-semibold mb-2"
            style={{ letterSpacing: "0.06em", fontSize: 11 }}
          >
            Bank accounts
          </p>
          <div className="table-responsive border rounded mb-2">
            <table className="table table-sm mb-0">
              <thead className="table-light">
                <tr>
                  <th className="small text-muted fw-semibold">Account name</th>
                  <th className="small text-muted fw-semibold">Bank name</th>
                  <th className="small text-muted fw-semibold">Account no.</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {banks.map((b, i) => (
                  <tr key={i}>
                    {["AccountName", "BankName", "AccountNo"].map((field) => (
                      <td key={field}>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={b[field]}
                          onChange={(e) => updateBank(i, field, e.target.value)}
                        />
                      </td>
                    ))}
                    <td className="text-center align-middle">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeBank(i)}
                        className="px-2 py-0"
                      >
                        ×
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button variant="link" size="sm" onClick={addBank} className="ps-0">
            + Add bank
          </Button>
        </Tab> */}

        {/* ── PREVIEW TAB ────────────────────────────────────────────────── */}
        <Tab eventKey="preview" title="Preview" onEnter={() => {}}>
          <div className="border rounded p-4 bg-white">
            <p
              className="text-uppercase text-muted small fw-semibold mb-3"
              style={{ letterSpacing: "0.05em", fontSize: 11 }}
            >
              Template
            </p>
            <div
              className="preview-body"
              style={{ fontSize: 14, lineHeight: 1.8, color: "#111" }}
              dangerouslySetInnerHTML={{
                __html: activeTab === "preview" ? getRenderedHtml() : "",
              }}
            />
          </div>
        </Tab>
      </Tabs>
    </>
  );
}
